import { Box, Skeleton, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  colorAccent,
  colorTextMuted,
  colorTextPrimary,
} from "src/design/tokens";

/** Today's booking counts, kept together so they cannot disagree. */
export interface HeaderBookingCounts {
  total: number;
  /** Bookings still awaiting confirmation. */
  pending: number;
}

interface DashboardHeaderProps {
  firstName: string | undefined;
  restaurantName: string | undefined;
  /**
   * Today's bookings. `undefined` when the viewer cannot see bookings at all,
   * which drops the sentence rather than showing a misleading zero.
   */
  bookings?: HeaderBookingCounts;
  isLoading: boolean;
}

export const DashboardHeader = ({
  firstName,
  restaurantName,
  bookings,
  isLoading,
}: DashboardHeaderProps) => {
  const { t } = useTranslation();

  const activity = restaurantName
    ? t("dashboard.header.activity", { restaurant: restaurantName })
    : "";
  // An empty day gets its own sentence: "every booking is confirmed" is true of
  // no bookings at all, but reads as reassurance about a service that does not
  // exist. Zero pending is spelled out too — i18next's plural categories have no
  // "zero" rule for English or French, so "0 bookings are waiting" would read
  // badly.
  const pending = !bookings
    ? ""
    : bookings.total === 0
      ? t("dashboard.header.noBookings")
      : bookings.pending === 0
        ? t("dashboard.header.noPending")
        : t("dashboard.header.pending", { count: bookings.pending });

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "12.5px",
          fontWeight: 600,
          color: colorAccent,
          letterSpacing: "0.3px",
        }}
      >
        {dayjs().format("dddd, MMMM D, YYYY")}
      </Typography>

      {isLoading ? (
        <Skeleton width={240} height={42} />
      ) : (
        <Typography
          sx={{
            fontSize: { xs: "22px", md: "28px" },
            fontWeight: 700,
            color: colorTextPrimary,
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          {firstName
            ? t("dashboard.header.greeting", { name: firstName })
            : t("dashboard.header.greetingFallback")}
        </Typography>
      )}

      {isLoading ? (
        <Skeleton width={360} height={22} />
      ) : (
        <Typography sx={{ fontSize: "14px", color: colorTextMuted, mt: 0.5 }}>
          {`${activity}${activity && pending ? " " : ""}${pending}`}
        </Typography>
      )}
    </Box>
  );
};
